import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, DatePicker, Dropdown, Table, Tag } from 'antd';
import { CarOutlined, MoreOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  updateOrderExpectedDelivery,
  type OrderDeliverySchedule,
  type OrderDeliveryScheduleLine,
  type OrderDeliveryScheduleTransport,
} from '../api/orderDeliveryScheduleApi';
import type { TransportRouteDetailDto } from '../../../types/transport';
import { TRANSPORT_DATE_DISPLAY_FORMAT, TRANSPORT_DATE_INPUT_FORMATS } from '../../../components/transportFormUtils';
import { getApiErrorMessage } from '../../../utils/apiError';
import { useFeedback } from '../../../utils/feedback';
import { getTransportLabel } from '../../../utils/transportLabel';
import { formatLocalizedNumber } from '../../../utils/numberFormatting';
import { formatCargoQuantityTotals } from '../../../utils/cargoQuantity';
import { buildTransportRouteLabel } from '../transportAddressUtils';
import { TransportAllocatedCost } from '../../transports/TransportAllocatedCost';
import { withTransportReturnContext } from '../../transports/transportReturnContext';
import {
  TRANSPORT_STATUS_DISPLAY_COLORS,
  getTransportStatusDisplay,
  getTransportStatusDisplayTranslationKey,
} from '../../transports/transportRouteViews';
import {
  deriveTransportKind,
  transportKindLabelKey,
  type TransportDirection,
} from '../../transports/transportDirection';
import OrderTransportStatusModal, { type OrderTransportStatusPayload } from '../sections/OrderTransportStatusModal';
import type { OrderProduct } from '../../../types/order';
import { getOrderProductDisplayDimensions } from '../orderProductDisplay';
import { OrderLineNameWithDimensions, useQuantityFormatter } from './OrderProcessTables';
import { Callout, StepSection } from './OrderProcessParts';

/** A table row: a truck of the order, or the part no truck carries yet. */
type ScheduleRow =
  | { key: string; kind: 'truck'; truck: OrderDeliveryScheduleTransport; detail: TransportRouteDetailDto | null }
  | { key: 'unassigned'; kind: 'unassigned'; lines: OrderDeliveryScheduleLine[]; forecastMonth?: string | null };

/** Dates as on the truck card and form („15.11.2026”). */
function formatTruckDate(value: string | null | undefined) {
  return value ? dayjs(value).format(TRANSPORT_DATE_DISPLAY_FORMAT) : '—';
}

/** „11.2026” — the forecast month as on the order card („Miesiąc prognozy”). */
function formatForecastMonth(month: string | null | undefined) {
  return month ? dayjs(`${month}-01`).format('MM.YYYY') : '—';
}

/**
 * „Transporty i terminy dostarczenia” on the ZK card (forecast by delivery,
 * owner 2026-10-16, docs/decisions/2026-10-16-prognoza-wedlug-dostaw.md).
 * Replaces the truck list of the hidden „Szczegóły zamówienia” drawer
 * (OrderTransportsSection). Owner 2026-10-16: the row stays short — truck,
 * goods, „Dostarczenie” (the truck's „Spodziewana data dostarczenia”, changed
 * in place), status with its actions („Zmień status”, open, unlink) and the
 * forecast month; carrier, route, loading day, delivered / not delivered,
 * load, this order's freight share and the other orders on the truck open
 * in the expanded row.
 *
 * Transport readers (ADMIN, OPERATIONS) get the full truck (`transports`);
 * the Handlowiec gets the trimmed schedule only — no details, cost, other
 * orders or truck card. The forecast month column is for forecast readers
 * (the backend sends `forecastMonth` to ADMIN only).
 */
export default function OrderDeliveryScheduleSection({
  schedule,
  transports = [],
  readOnly,
  canOpenTransports,
  canAddTransport,
  canViewCosts = false,
  products = [],
  onOpenTransportLink,
  onStatusChange,
  onUnlink,
  onChanged,
}: {
  schedule: OrderDeliverySchedule;
  /** Full trucks of the order (transport readers); empty for the Handlowiec. */
  transports?: TransportRouteDetailDto[];
  readOnly: boolean;
  /** Truck cards are for transport readers (ADMIN, OPERATIONS); others see the label only. */
  canOpenTransports: boolean;
  canAddTransport: boolean;
  canViewCosts?: boolean;
  /** Order lines, for the dimensions under the product names (joined by `orderProductId`). */
  products?: OrderProduct[];
  onOpenTransportLink?: () => void;
  /** „Zmień status” (orderTransportActions); absent = no status change. */
  onStatusChange?: (transportId: string, payload: OrderTransportStatusPayload) => Promise<boolean>;
  /** „Odepnij od zamówienia” (orderTransportActions); absent = no unlink. */
  onUnlink?: (transportId: string) => Promise<void> | void;
  onChanged?: () => void;
}) {
  const { t, i18n } = useTranslation(['orders', 'transport', 'common']);
  const navigate = useNavigate();
  const feedback = useFeedback();
  const quantity = useQuantityFormatter();
  const [current, setCurrent] = useState(schedule);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [statusTruckId, setStatusTruckId] = useState<string | null>(null);
  // The picker can report the same typed date twice (Enter, then blur): one save per date.
  const pendingDates = useRef(new Map<string, string | null>());
  useEffect(() => setCurrent(schedule), [schedule]);

  const detailById = new Map(transports.map((truck) => [truck.id, truck]));
  const productsById = new Map(products.map((product) => [product.id, product]));
  const lineProduct = (line: OrderDeliveryScheduleLine) => (line.orderProductId ? productsById.get(line.orderProductId) : undefined);
  const showForecast = current.unassignedForecastMonth !== undefined;
  const canEdit = current.canEditExpectedDelivery && !readOnly;
  const statusTruck = statusTruckId ? detailById.get(statusTruckId) ?? null : null;
  const truckHref = (truckId: string) => withTransportReturnContext(`/app/transports/${encodeURIComponent(truckId)}`, current.orderId);

  const saveDate = async (truck: OrderDeliveryScheduleTransport, value: Dayjs | null) => {
    const label = getTransportLabel(truck, t('orders:process.transport.unnamed'));
    const date = value ? value.format('YYYY-MM-DD') : null;
    if (pendingDates.current.has(truck.id) && pendingDates.current.get(truck.id) === date) return;
    if (!pendingDates.current.has(truck.id) && (truck.estimatedDelivery?.slice(0, 10) ?? null) === date) return;
    pendingDates.current.set(truck.id, date);
    setSavingId(truck.id);
    try {
      const next = await updateOrderExpectedDelivery(current.orderId, truck.id, date);
      setCurrent(next);
      feedback.success(t('orders:process.deliverySchedule.saved', { label }));
      onChanged?.();
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'orders:process.deliverySchedule.saveFailed', {
        TRANSPORT_ALREADY_DELIVERED: 'orders:process.deliverySchedule.alreadyDelivered',
        TRANSPORT_NOT_ON_ORDER: 'orders:process.deliverySchedule.notOnOrder',
      }, { label }));
    } finally {
      pendingDates.current.delete(truck.id);
      setSavingId(null);
    }
  };

  const goods = (lines: OrderDeliveryScheduleLine[]) => (
    <div className="zk-delivery-lines">
      {lines.map((line, index) => (
        <OrderLineNameWithDimensions
          key={`${line.orderProductId || line.productName}-${index}`}
          name={<>{line.productName} <span className="zk-num">{quantity(line.quantity, line.unit)}</span></>}
          product={lineProduct(line)}
          testId="zk-delivery-goods-line-dimensions"
        />
      ))}
    </div>
  );

  /** Expanded row: everything the drawer card showed about this truck and this order. */
  const truckDetails = (truck: OrderDeliveryScheduleTransport, detail: TransportRouteDetailDto) => {
    const routeOrder = detail.routeOrders.find((item) => item.orderId === current.orderId);
    const others = detail.routeOrders.filter((item) => item.orderId !== current.orderId);
    const route = buildTransportRouteLabel(detail.loadingAddresses, detail.deliveryAddresses, detail.originCity, detail.destinationCity).label;
    const totals = routeOrder?.totalQuantityByUnit
      ? formatCargoQuantityTotals(routeOrder.totalQuantityByUnit as Record<string, number>, i18n.language, ' · ')
      : '-';
    const delivered = truck.lines.filter((line) => line.deliveredQuantity != null);
    const facts = [
      [t('orders:process.transport.carrier'), detail.transportCompany || t('orders:process.transport.carrierMissing')],
      [t('orders:process.deliverySchedule.route'), route || '—'],
      [t('transport:fields.loadingDate'), formatTruckDate(truck.loadingDate)],
      totals !== '-' ? [t('transport:cargoQuantity'), totals] : null,
      routeOrder?.weight ? [t('transport:weight'), `${formatLocalizedNumber(routeOrder.weight, 2, i18n.language)} kg`] : null,
      routeOrder?.volume ? [t('transport:volume'), `${formatLocalizedNumber(routeOrder.volume, 3, i18n.language)} m³`] : null,
      routeOrder?.allocationPercent ? [t('transport:allocation.percent'), `${formatLocalizedNumber(routeOrder.allocationPercent, 2, i18n.language)}%`] : null,
    ].filter(Boolean) as Array<[string, string]>;
    return (
      <div className="zk-delivery-detail" data-testid={`zk-delivery-detail-${detail.id}`}>
        {facts.map(([label, value]) => (
          <span key={label} className={label === t('orders:process.deliverySchedule.route') ? 'zk-delivery-detail__wide' : undefined}>
            <small>{label}</small><b>{value}</b>
          </span>
        ))}
        {delivered.length > 0 && (
          <span className="zk-delivery-detail__wide">
            <small>{t('transport:orderTransports.actuallyDelivered')}</small>
            <b>
              {delivered.map((line, index) => {
                const missing = Math.max((line.quantity ?? 0) - (line.deliveredQuantity ?? 0), 0);
                const dimensions = getOrderProductDisplayDimensions(lineProduct(line));
                return (
                  <React.Fragment key={`${line.orderProductId || line.productName}-${index}`}>
                    {index > 0 && '; '}
                    {line.productName}
                    {dimensions && <small className="zk-delivery-dimensions" data-testid="zk-delivery-delivered-line-dimensions"> ({dimensions})</small>}
                    {' '}<span className="zk-num">{quantity(line.deliveredQuantity, line.unit)}</span>
                    {missing > 0 && (
                      <span className="zk-delivery-missing">
                        {' '}({t('transport:orderTransports.notDelivered').toLowerCase()} <span className="zk-num">{quantity(missing, line.unit)}</span>)
                      </span>
                    )}
                  </React.Fragment>
                );
              })}
            </b>
          </span>
        )}
        {canViewCosts && Number(routeOrder?.allocatedTransportCost || 0) > 0 && (
          <span>
            <small>{t('orders:process.deliverySchedule.freightShare')}</small>
            <TransportAllocatedCost
              allocatedTransportCost={routeOrder?.allocatedTransportCost as number}
              originalCurrency={routeOrder?.originalCurrency}
              originalAmount={routeOrder?.originalAmount}
              exchangeRateUsed={routeOrder?.exchangeRateUsed}
              exchangeRateSource={routeOrder?.exchangeRateSource}
              exchangeRateTableNumber={routeOrder?.exchangeRateTableNumber}
              exchangeRateRequestedDate={routeOrder?.exchangeRateRequestedDate}
              exchangeRateEffectiveDate={routeOrder?.exchangeRateEffectiveDate}
              exchangeRateIsFallback={routeOrder?.exchangeRateIsFallback}
            />
          </span>
        )}
        {others.length > 0 && (
          <span className="zk-delivery-detail__wide">
            <small>{t('orders:process.deliverySchedule.otherOrders')}</small>
            <b>
              {others.map((item, index) => (
                <React.Fragment key={item.orderId}>
                  {index > 0 && ', '}
                  <Link to={`/app/orders/${encodeURIComponent(item.orderId)}`}>{item.order?.displayOrderNumber || item.order?.orderNumber}</Link>
                  {item.order?.customerName ? ` (${item.order.customerName})` : ''}
                </React.Fragment>
              ))}
            </b>
          </span>
        )}
      </div>
    );
  };

  const rows: ScheduleRow[] = [
    ...current.transports.map((truck) => ({ key: truck.id, kind: 'truck' as const, truck, detail: detailById.get(truck.id) ?? null })),
    ...(current.unassigned.length > 0
      ? [{ key: 'unassigned' as const, kind: 'unassigned' as const, lines: current.unassigned, forecastMonth: current.unassignedForecastMonth }]
      : []),
  ];

  return (
    <StepSection
      title={t('orders:process.deliverySchedule.title')}
      icon={<CarOutlined />}
      testId="zk-delivery-schedule"
      hint={t(showForecast ? 'orders:process.deliverySchedule.hint' : 'orders:process.deliverySchedule.hintNoForecast')}
      aside={!readOnly && canAddTransport && onOpenTransportLink && (
        <Button size="small" icon={<CarOutlined />} onClick={onOpenTransportLink}>
          {current.transports.length ? t('orders:process.ready.addTransport') : t('orders:process.ready.planTransport')}
        </Button>
      )}
    >
      {rows.length === 0 ? (
        <Callout tone="warning" flush>{t('orders:process.deliverySchedule.empty')}</Callout>
      ) : (
        <Table<ScheduleRow>
          size="small"
          rowKey="key"
          pagination={false}
          dataSource={rows}
          className="zk-delivery-table"
          data-testid="zk-delivery-schedule-table"
          expandable={transports.length > 0 ? {
            rowExpandable: (row) => row.kind === 'truck' && Boolean(row.detail),
            expandedRowRender: (row) => (row.kind === 'truck' && row.detail ? truckDetails(row.truck, row.detail) : null),
          } : undefined}
          columns={[
            {
              title: t('orders:process.deliverySchedule.truck'),
              key: 'truck',
              render: (_v, row) => {
                if (row.kind === 'unassigned') {
                  return <span className="zk-delivery-unassigned">{t('orders:process.deliverySchedule.unassigned')}</span>;
                }
                const { truck } = row;
                const label = getTransportLabel(truck, t('orders:process.transport.unnamed'));
                const kind = deriveTransportKind({
                  direction: truck.direction as TransportDirection | null,
                  customerOrderCount: truck.customerOrderCount,
                  supplierOrderCount: truck.supplierOrderCount,
                });
                return (
                  <div className="zk-line-identity">
                    {canOpenTransports
                      ? <Link to={truckHref(truck.id)} className="zk-num">{label}</Link>
                      : <span className="zk-num">{label}</span>}
                    <small>{t(transportKindLabelKey(kind))}</small>
                  </div>
                );
              },
            },
            {
              title: t('orders:process.deliverySchedule.goods'),
              key: 'goods',
              render: (_v, row) => {
                if (row.kind === 'unassigned') return goods(row.lines);
                return row.truck.wholeOrder
                  ? (
                    <div className="zk-line-identity">
                      <span>{t('orders:process.deliverySchedule.wholeOrder')}</span>
                      <small>{t('orders:process.deliverySchedule.wholeOrderHint')}</small>
                    </div>
                  )
                  : goods(row.truck.lines);
              },
            },
            {
              title: t('orders:process.deliverySchedule.date'),
              key: 'date',
              className: 'zk-nowrap-header',
              render: (_v, row) => {
                if (row.kind === 'unassigned') return <span className="zk-delivery-muted">—</span>;
                const { truck } = row;
                if (truck.status === 'DELIVERED') {
                  return <span className="zk-num" title={t('transport:fields.actualDelivery')}>{formatTruckDate(truck.actualDelivery)}</span>;
                }
                if (!canEdit) {
                  return <span className="zk-num">{truck.estimatedDelivery ? formatTruckDate(truck.estimatedDelivery) : t('orders:process.schedule.noDate')}</span>;
                }
                return (
                  <DatePicker
                    size="small"
                    variant="borderless"
                    suffixIcon={null}
                    allowClear={false}
                    className="zk-delivery-date"
                    format={TRANSPORT_DATE_INPUT_FORMATS}
                    // Delivery cannot come before loading (review PR #381).
                    disabledDate={(day) => Boolean(truck.loadingDate) && day.isBefore(dayjs(truck.loadingDate), 'day')}
                    value={truck.estimatedDelivery ? dayjs(truck.estimatedDelivery) : null}
                    disabled={savingId === truck.id}
                    placeholder={t('orders:process.deliverySchedule.pickDate')}
                    aria-label={t('orders:process.deliverySchedule.dateAria', { label: getTransportLabel(truck, '') })}
                    data-testid={`zk-delivery-date-${truck.id}`}
                    onChange={(value) => { void saveDate(truck, value); }}
                  />
                );
              },
            },
            {
              title: t('orders:process.transport.status'),
              key: 'status',
              render: (_v, row) => {
                if (row.kind === 'unassigned') return null;
                const { truck, detail } = row;
                const statusKey = getTransportStatusDisplay(truck.status);
                const label = getTransportLabel(truck, t('orders:process.transport.unnamed'));
                const menuItems = readOnly ? [] : [
                  ...(onStatusChange && detail ? [{ key: 'status', label: t('transport:orderTransports.changeStatus') }] : []),
                  ...(canOpenTransports ? [{ key: 'open', label: t('orders:process.deliverySchedule.openTruck') }] : []),
                  ...(onUnlink ? [{ key: 'unlink', danger: true, label: t('orders:process.deliverySchedule.unlink') }] : []),
                ];
                return (
                  <div className="zk-delivery-actions">
                    <Tag color={TRANSPORT_STATUS_DISPLAY_COLORS[statusKey]}>{t(getTransportStatusDisplayTranslationKey(truck.status))}</Tag>
                    {menuItems.length > 0 && (
                      <Dropdown
                        trigger={['click']}
                        menu={{
                          items: menuItems,
                          onClick: ({ key }) => {
                            if (key === 'status') setStatusTruckId(truck.id);
                            if (key === 'open') navigate(truckHref(truck.id));
                            if (key === 'unlink') void onUnlink?.(truck.id);
                          },
                        }}
                      >
                        <Button size="small" type="text" icon={<MoreOutlined />} aria-label={t('orders:process.deliverySchedule.moreActions', { label })} data-testid={`zk-delivery-more-${truck.id}`} />
                      </Dropdown>
                    )}
                  </div>
                );
              },
            },
            ...(showForecast ? [{
              title: t('orders:process.deliverySchedule.forecastMonth'),
              key: 'forecast',
              align: 'right' as const,
              className: 'zk-nowrap-header',
              render: (_v: unknown, row: ScheduleRow) => (
                <span className="zk-num">
                  {formatForecastMonth(row.kind === 'truck' ? row.truck.forecastMonth : row.forecastMonth)}
                </span>
              ),
            }] : []),
          ]}
        />
      )}
      {onStatusChange && (
        <OrderTransportStatusModal
          transport={statusTruck}
          onClose={() => setStatusTruckId(null)}
          onSave={async (transportId, payload) => {
            const saved = await onStatusChange(transportId, payload);
            if (saved) onChanged?.();
            return saved;
          }}
        />
      )}
    </StepSection>
  );
}
